import React, { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Typography, Button } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { homePopupService } from '../services/api';
import { getImageUrl } from '../utils/assets';

const BRAND_GREEN = '#477C2E';

function dismissKey(popup) {
  return `rwf-home-popup-${popup.id}-${popup.updated_at || ''}`;
}

function openLink(link, navigate) {
  const raw = (link || '').trim();
  if (!raw) return;
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    window.location.href = raw;
    return;
  }
  navigate(raw.startsWith('/') ? raw : `/${raw}`);
}

const HomePopup = () => {
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    homePopupService
      .getActive()
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        const next = Array.isArray(list) ? list[0] : null;
        if (cancelled || !next) return;
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(dismissKey(next))) return;
        setPopup(next);
        setOpen(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClose = () => {
    if (popup && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(dismissKey(popup), '1');
    }
    setOpen(false);
  };

  if (!popup) return null;

  const image = getImageUrl(popup.image_url);
  const title = (popup.title || '').trim();
  const content = (popup.content || '').trim();
  const buttonText = (popup.button_text || '').trim();
  const buttonLink = (popup.button_link || '').trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={title ? 'home-popup-title' : undefined}
      aria-label={title ? undefined : 'Announcement'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          m: 2,
          position: 'relative',
          maxWidth: 520,
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        aria-label="Close popup"
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 2,
          width: 36,
          height: 36,
          backgroundColor: 'rgba(255,255,255,0.95)',
          color: '#2D2929',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          '&:hover': { backgroundColor: '#fff' },
        }}
      >
        <Close fontSize="small" />
      </IconButton>

      {image && (
        <Box
          component="img"
          src={image}
          alt={title || 'Announcement'}
          sx={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block', bgcolor: '#f3f4f6' }}
        />
      )}

      {(title || content || buttonText) && (
        <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 3, pr: image ? undefined : 6 }}>
          {title && (
            <Typography
              id="home-popup-title"
              sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: 20, sm: 22 }, color: '#2D2929', mb: content ? 1 : 0, pr: image ? 0 : 1 }}
            >
              {title}
            </Typography>
          )}
          {content && (
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 400,
                fontSize: 15,
                lineHeight: 1.6,
                color: '#4B4646',
                whiteSpace: 'pre-wrap',
              }}
            >
              {content}
            </Typography>
          )}
          {buttonText && buttonLink && (
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                handleClose();
                openLink(buttonLink, navigate);
              }}
              sx={{
                mt: 2.5,
                borderRadius: 999,
                backgroundColor: BRAND_GREEN,
                textTransform: 'none',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                px: 3,
                '&:hover': { backgroundColor: '#3a6625' },
              }}
            >
              {buttonText}
            </Button>
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default HomePopup;
